import { developerPortal } from '@/content/developerPortal'

export const idpIntegration = {
  eyebrow: 'Platform integration',
  title: 'Identity provider integration',
  lead:
    'How NODEMOS connects sign-in for your portals — built-in social providers, external identity providers, and role-aware access from your first pilot.',
  developerPortalPath: '/identity',
  sections: [
    {
      heading: 'Overview',
      paragraphs: [
        'Secure sign-in is a platform foundation, not a late-stage add-on. Every portal gets identity provision and identity checking — who can sign in, how they are verified, and how access is scoped across your product.',
        'You can offer Google, Apple, and GitHub sign-in out of the box, connect an external identity provider your organisation already runs, or combine both in the same product.',
      ],
    },
    {
      heading: 'Sign-in options',
      list: [
        'Google — OAuth 2.0 / OpenID Connect',
        'Apple — Sign in with Apple',
        'GitHub — OAuth for developer-facing products',
        'External identity provider — your corporate IdP via OIDC or SAML (Azure AD, Okta, Keycloak, and similar)',
      ],
      afterList: [
        'Built-in social sign-in and an external identity provider can run side by side. Corporate users authenticate through your directory; customers or partners use the channels that fit them.',
      ],
    },
    {
      heading: 'What we configure',
      list: [
        'Portal-level access rules and role mapping',
        'Session management and token lifecycle',
        'Identity verification flows where your product requires them',
        'Federation metadata exchange for OIDC and SAML providers',
        'Audit-friendly sign-in events across portals',
      ],
    },
    {
      heading: 'Protocols and standards',
      paragraphs: [
        'Integrations are standards-based so your IdP team can review them without proprietary lock-in.',
      ],
      list: [
        'OpenID Connect (OIDC)',
        'OAuth 2.0',
        'SAML 2.0 single sign-on',
      ],
    },
    {
      heading: 'Developer portal',
      paragraphs: [
        `API reference, webhook schemas, environment setup, and test credentials for identity integrations are published in the ${developerPortal.label}.`,
        'The portal is being prepared — the link below is a placeholder and will be updated when documentation goes live.',
      ],
    },
    {
      heading: 'Typical setup flow',
      list: [
        'Define which portals need sign-in and which roles each portal serves',
        'Choose built-in providers, an external identity provider, or both',
        'Exchange federation metadata and map claims to product roles',
        'Validate sign-in, session, and access rules in your first pilot environment',
      ],
      afterList: [
        'For enterprise IdP setups or custom claim mapping, contact us — we work directly with your identity team during architecture and pilot.',
      ],
    },
  ],
  relatedLinks: [
    { label: 'How we work', to: '/how-we-work', icon: 'fa-solid fa-compass' },
    { label: 'Configure your product', to: '/onboarding', icon: 'fa-solid fa-sitemap' },
    { label: 'Payment provider integration', to: '/integrations/payment-provider', icon: 'fa-solid fa-credit-card' },
  ],
}

export const paymentProviderIntegration = {
  eyebrow: 'Platform integration',
  title: 'Payment provider API integration',
  lead:
    'How NODEMOS wires payment handling into your product — subscriptions, one-off charges, invoicing, and marketplace settlements shaped to your business model.',
  developerPortalPath: '/payments',
  sections: [
    {
      heading: 'Overview',
      paragraphs: [
        'Payment flows are composed into your product when revenue is part of the journey — not pasted on as a generic checkout at the end.',
        'We integrate with payment providers through their APIs so charges, subscriptions, and settlements follow your pricing model and operational process from the first pilot that needs them.',
      ],
    },
    {
      heading: 'What we support',
      list: [
        'One-off charges and checkout flows',
        'Subscriptions and recurring billing',
        'Invoicing and payment collection',
        'Marketplace payouts and split settlements',
        'Webhook-driven status updates inside your product',
      ],
      afterList: [
        'The right payment provider and API surface depend on your geography, compliance needs, and pricing model — we select and configure during architecture, not as an afterthought.',
      ],
    },
    {
      heading: 'How the API integration works',
      list: [
        'Provider credentials and environments scoped per deployment',
        'Server-side API calls — card data never passes through your application layer unnecessarily',
        'Idempotent charge and subscription operations with audit trails',
        'Webhook ingestion for payment, refund, and dispute events',
        'Product-native payment state surfaced in the portals that need it',
      ],
    },
    {
      heading: 'Compliance and operations',
      paragraphs: [
        'Payment integrations are designed around provider-hosted flows and server-side API usage so PCI scope stays manageable.',
      ],
      list: [
        'EU data residency and GDPR considerations mapped during architecture',
        'Reconciliation hooks for finance and operations teams',
        'Test and production environment separation from first pilot onward',
      ],
    },
    {
      heading: 'Developer portal',
      paragraphs: [
        `Payment API endpoints, webhook payloads, sandbox setup, and event reference are published in the ${developerPortal.label}.`,
        'The portal is being prepared — the link below is a placeholder and will be updated when documentation goes live.',
      ],
    },
    {
      heading: 'Typical setup flow',
      list: [
        'Define pricing model — one-off, subscription, invoice, or marketplace',
        'Select payment provider and API integration scope',
        'Configure webhooks and product-side payment state',
        'Validate charges, refunds, and settlement flows in pilot',
      ],
      afterList: [
        'Tell us your payment requirements in the product configuration flow — we map them into architecture and pilot scope.',
      ],
    },
  ],
  relatedLinks: [
    { label: 'How we work', to: '/how-we-work', icon: 'fa-solid fa-compass' },
    { label: 'Configure your product', to: '/onboarding', icon: 'fa-solid fa-sitemap' },
    { label: 'Identity provider integration', to: '/integrations/identity-provider', icon: 'fa-solid fa-shield-halved' },
  ],
}